// Shared type-to-filter combobox — a small presentational shell over the pure
// filter + keyboard math in ./comboboxLogic (100% unit-covered). Replaces a
// plain <select> where the option list is long enough that scanning is painful
// (agents / datasets). Keyboard: Up/Down move the highlight, Enter selects,
// Escape closes; it also closes on blur and on select. ARIA combobox/listbox.
//
// Props:
//   options    [{ id, label }]  — normalize raw rows with toOptions() first
//   value      currently-selected id (or "")
//   onChange   (id) => void      — fired on select
//   disabled   boolean
//   placeholder string
//   idPrefix   string            — namespaces the listbox + option ids
//   minWidth   number            — root min width (px)
import { useState, useRef } from "react";
import { COLORS } from "../theme/colors";
import { filterOptions, nextHighlight } from "./comboboxLogic";

export default function SearchableSelect({
  options,
  value,
  onChange,
  disabled = false,
  placeholder = "Search…",
  idPrefix = "ss",
  minWidth = 210,
  inputStyle,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(-1);
  const blurTimer = useRef(null);

  const list = Array.isArray(options) ? options.filter(Boolean) : [];
  const selected = list.find((o) => o && o.id === value) || null;
  const selectedLabel = selected ? String(selected.label ?? selected.id) : "";
  const filtered = filterOptions(list, open ? query : "");

  const close = () => { setOpen(false); setQuery(""); setHighlight(-1); };
  const commit = (o) => { if (o) onChange(o.id); close(); };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHighlight((h) => nextHighlight(h, e.key === "ArrowDown" ? 1 : -1, filtered.length));
    } else if (e.key === "Enter") {
      if (open && highlight >= 0 && filtered[highlight]) { e.preventDefault(); commit(filtered[highlight]); }
    } else if (e.key === "Escape") {
      if (open) { e.preventDefault(); close(); }
    }
  };

  const listboxId = `${idPrefix}-listbox`;
  return (
    <div
      style={{ position: "relative", minWidth }}
      onBlur={() => { blurTimer.current = setTimeout(close, 120); }}
      onFocus={() => { if (blurTimer.current) clearTimeout(blurTimer.current); }}
    >
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={open && highlight >= 0 ? `${idPrefix}-opt-${highlight}` : undefined}
        disabled={disabled}
        placeholder={list.length === 0 ? "No options" : placeholder}
        value={open ? query : selectedLabel}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(-1); }}
        onFocus={() => !disabled && setOpen(true)}
        onClick={() => !disabled && setOpen(true)}
        onKeyDown={onKeyDown}
        style={{
          font: "inherit", fontSize: 12.5, color: COLORS.text, background: COLORS.surfaceHover,
          border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 10px",
          width: "100%", cursor: disabled ? "default" : "text", ...inputStyle,
        }}
      />
      {open && !disabled && (
        <ul
          id={listboxId}
          role="listbox"
          style={{
            position: "absolute", zIndex: 30, top: "calc(100% + 4px)", left: 0, right: 0,
            margin: 0, padding: 4, listStyle: "none", maxHeight: 260, overflowY: "auto",
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            borderRadius: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          }}
        >
          {filtered.length === 0 ? (
            <li style={{ padding: "8px 10px", fontSize: 12, color: COLORS.textDim }}>
              No matches
            </li>
          ) : filtered.map((o, i) => {
            // Compute per-row values as plain statements (not inline JSX
            // expressions) so each executes on every render and is covered
            // independently of how v8 attributes JSX sub-expressions to source
            // lines across node versions (the diff-coverage line-mapping trap).
            const active = i === highlight;
            const isSel = o.id === value;
            const optKey = o.id == null ? i : o.id;
            const optLabel = String(o.label == null ? o.id : o.label);
            const hoverBg = COLORS.surfaceHover || "rgba(124,58,237,0.12)";
            const rowBg = active ? hoverBg : "transparent";
            return (
              <li
                key={optKey}
                id={`${idPrefix}-opt-${i}`}
                role="option"
                aria-selected={isSel}
                onMouseDown={(e) => { e.preventDefault(); commit(o); }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  padding: "7px 10px", fontSize: 13, borderRadius: 4, cursor: "pointer",
                  color: COLORS.text, fontWeight: isSel ? 700 : 500,
                  background: rowBg,
                }}
              >
                {optLabel}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
